import { useState, useCallback } from 'react';

export const useFormHandler = (initialState, validationRules = {}) => {
  const [formData, setFormData] = useState(initialState);
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});

  const handleChange = useCallback((e) => {
    const { name, value, type, files } = e.target;
    
    if (type === 'file') {
      setFormData((prev) => ({ ...prev, [name]: files[0] }));
    } else {
      setFormData((prev) => ({ ...prev, [name]: value }));
    }

    // Clear error for this field when user starts typing
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: '' }));
    }
  }, [errors]);

  const handleBlur = useCallback((e) => {
    const { name } = e.target;
    setTouched((prev) => ({ ...prev, [name]: true }));
  }, []);

  const validateField = useCallback((name, value) => {
    const rules = validationRules[name];
    if (!rules) return '';

    for (const rule of rules) {
      const error = rule(value, formData);
      if (error) return error;
    }
    return '';
  }, [validationRules, formData]);

  const validateForm = useCallback(() => {
    const newErrors = {};
    let isValid = true;

    Object.keys(validationRules).forEach((fieldName) => {
      const error = validateField(fieldName, formData[fieldName]);
      if (error) {
        newErrors[fieldName] = error;
        isValid = false;
      }
    });

    setErrors(newErrors);
    return isValid;
  }, [formData, validationRules, validateField]);

  const resetForm = useCallback(() => {
    setFormData(initialState);
    setErrors({});
    setTouched({});
  }, [initialState]);

  const setFormValues = useCallback((values) => {
    setFormData((prev) => ({ ...prev, ...values }));
  }, []);

  return {
    formData,
    errors,
    touched,
    handleChange,
    handleBlur,
    validateForm,
    resetForm,
    setFormValues,
    setErrors,
  };
};

// Common validation rules
export const validationRules = {
  required: (fieldName) => (value) => {
    if (!value || (typeof value === 'string' && !value.trim())) {
      return `${fieldName} is required`;
    }
    return '';
  },

  minLength: (min, fieldName) => (value) => {
    if (value && value.length < min) {
      return `${fieldName} must be at least ${min} characters`;
    }
    return '';
  },

  maxLength: (max, fieldName) => (value) => {
    if (value && value.length > max) {
      return `${fieldName} must be at most ${max} characters`;
    }
    return '';
  },

  phoneNumber: () => (value) => {
    if (value && !value.match(/^\d{10}$/)) {
      return 'Enter a valid 10-digit mobile number';
    }
    return '';
  },

  email: () => (value) => {
    if (value && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) {
      return 'Enter a valid email address';
    }
    return '';
  },

  fileSize: (maxSizeMB) => (file) => {
    if (file && file instanceof File && file.size > maxSizeMB * 1024 * 1024) {
      return `File size must be less than ${maxSizeMB}MB`;
    }
    return '';
  },

  fileType: (allowedTypes) => (file) => {
    if (file && file instanceof File) {
      const fileType = file.type.split('/')[0];
      if (!allowedTypes.includes(fileType)) {
        return `File must be of type: ${allowedTypes.join(', ')}`;
      }
    }
    return '';
  },
};